/**
 * MomentVideo - the video half of every moment viewer.
 *
 * It is no longer a <video> of its own: it HOSTS the one shared element from
 * `utils/sharedMomentVideo` (BUG-076). Three screens mount it — the sequence
 * viewer, Memories' MediaViewer and the single-moment view in a conversation —
 * and whichever is on screen adopts the same element, so the permission to
 * make noise, granted once by the tap that opened the moment, is never thrown
 * away by a photo appearing between two videos.
 *
 * Supersedes BUG-030's design (v2.5.0), which was the honest answer when
 * playback started from an effect: begin silent, offer "Tap for sound", hold
 * on a Replay button at the end. All three are gone. Sound is on by default
 * and unmuted from the opening gesture (`unlockMomentAudio`), mute is a
 * state-truthful icon in the header chrome of each viewer, and a finished
 * video hands back to `onEnded` so the viewer advances like a photo does.
 *
 * `playbackKey` — the moment id, not the src — decides when playback restarts:
 * video A → photo → back to A is the same blob URL, and it's the key going
 * A → null → A that has to replay it from the first frame.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { adoptSharedVideo, releaseSharedVideo } from '../utils/sharedMomentVideo';
import { useMediaFit } from '../hooks/useMediaFit';

export interface MomentVideoProps {
  /** The blob URL to play, or null when a photo is showing (element hides). */
  src: string | null;
  /** Identity of what's playing. A change (including to/from null) restarts. */
  playbackKey?: number | string | null;
  /** Held by the viewer (finger down to pause) */
  paused?: boolean;
  /**
   * The moment's thumbhash. Only its ENCODED ASPECT is read here — it tells us
   * whether this is a wide moment before the first frame decodes, so the fit
   * never visibly flips (FEAT-027). `poster` still carries the picture.
   */
  thumbhash?: string | null;
  /** Viewer-owned sound state; default is sound ON */
  muted?: boolean;
  /** Memories loops; a received moment plays once */
  loop?: boolean;
  /**
   * Classes for the <video> itself. Left unset (the received-moment viewers),
   * the element fills the screen and the FIT follows the moment's shape — see
   * `utils/mediaFit`. Passed explicitly (Memories, which letterboxes every
   * shape inside a swipe carousel), it is used verbatim.
   */
  className?: string;
  /** Instant blur placeholder while the first frame decodes (BUG-061) */
  poster?: string;
  /** Playback reached the end — the viewer decides what happens next */
  onEnded?: () => void;
  /**
   * Playback position as a 0..1 fraction, straight off `timeupdate`. The
   * viewer's progress bar rides this instead of hanging its own listener on
   * an element that lives outside React's tree.
   */
  onTimeUpdate?: (fraction: number) => void;
  /**
   * The first frame is decoded and paintable (`loadeddata`). For a STREAMED
   * source (FEAT-028 wave 6: src is the API URL, not a blob) this is the only
   * honest "it's on screen" signal — view-marking gates on it, because a
   * video that never loads must not burn the moment.
   */
  onFirstFrame?: () => void;
  /** Decode/playback failure — the viewer shows its error state */
  onPlaybackError?: () => void;
  /** Sound was refused and we fell back to muted, so no icon has to lie */
  onAutoplayBlocked?: () => void;
  /**
   * The decoder knows the clip's real size — same shape as an <img>'s
   * `onLoad={(e) => noteMedia(e.currentTarget)}`, so a host can feed its own
   * `useMediaFit`. The overlay layer needs it: it draws the grey bar on the
   * rect the picture occupies, and the thumbhash's approximate aspect is not
   * precise enough to line up with (FEAT-028 wave 2).
   */
  onMediaElement?: (el: HTMLVideoElement) => void;
}

export const MomentVideo = forwardRef<HTMLVideoElement, MomentVideoProps>(
  function MomentVideo(
    {
      src,
      playbackKey = null,
      paused = false,
      thumbhash = null,
      muted = false,
      loop = false,
      className,
      poster,
      onEnded,
      onTimeUpdate,
      onFirstFrame,
      onPlaybackError,
      onAutoplayBlocked,
      onMediaElement,
    },
    forwardedRef
  ) {
    const hostRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // Portrait fills the screen and always has (a phone's 9:16 is narrower
    // than the screen, and "fit" left bands with the controls stranded in
    // them — Nation's call). A wide moment cannot take that deal, because
    // cover would crop 16:9 to its middle third, so it letterboxes instead.
    const { fitClass, noteMedia } = useMediaFit(thumbhash, playbackKey);
    const resolvedClass = className ?? `h-full w-full ${fitClass}`;

    // Read inside effects without making them re-run
    const pausedRef = useRef(paused);
    pausedRef.current = paused;
    const mutedRef = useRef(muted);
    mutedRef.current = muted;
    const handlersRef = useRef({
      onEnded,
      onTimeUpdate,
      onFirstFrame,
      onPlaybackError,
      onAutoplayBlocked,
      onMediaElement,
    });
    handlersRef.current = {
      onEnded,
      onTimeUpdate,
      onFirstFrame,
      onPlaybackError,
      onAutoplayBlocked,
      onMediaElement,
    };

    // Adopt BEFORE anything reads the ref — the sequence viewer's progress
    // effect wants the element on the same commit the host appears.
    useLayoutEffect(() => {
      const host = hostRef.current;
      if (!host) return;
      videoRef.current = adoptSharedVideo(host);
      return () => {
        videoRef.current = null;
        releaseSharedVideo(host);
      };
    }, []);

    useImperativeHandle(forwardedRef, () => videoRef.current as HTMLVideoElement, []);

    // The element lives outside React's tree, so its attributes are set here
    // rather than rendered.
    useLayoutEffect(() => {
      const video = videoRef.current;
      if (!video) return;
      video.className = resolvedClass;
      // iOS puts an inline PLAYING video on its own hardware plane, painted
      // over every non-composited sibling regardless of DOM order — the
      // caption band was invisible on videos even with its canvas composited
      // (FEAT-040 gate, round 2). A transform demotes the video into the
      // normal compositing tree, where paint order applies again.
      video.style.transform = 'translateZ(0)';
      video.loop = loop;
      video.style.display = src ? '' : 'none';
      if (poster) video.setAttribute('poster', poster);
      else video.removeAttribute('poster');
    }, [resolvedClass, loop, poster, src]);

    // One set of listeners for the life of the host
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handleEnded = () => {
        // A stale end from an element showing no moment must not advance
        // whatever replaced it (BUG-125's skip, made structural).
        if (!video.getAttribute('src')) return;
        handlersRef.current.onEnded?.();
      };
      // The real shape, the moment the decoder knows it — replaces the
      // thumbhash's approximation.
      const handleMetadata = () => {
        noteMedia(video);
        handlersRef.current.onMediaElement?.(video);
      };
      const handleError = () => {
        // Clearing src to hand the element back fires an error in some
        // browsers — a player with nothing loaded has nothing to fail at.
        if (!video.getAttribute('src')) return;
        handlersRef.current.onPlaybackError?.();
      };
      const handleLoadedData = () => {
        if (!video.getAttribute('src')) return;
        handlersRef.current.onFirstFrame?.();
      };
      const handleTimeUpdate = () => {
        if (!video.getAttribute('src') || !(video.duration > 0)) return;
        handlersRef.current.onTimeUpdate?.(video.currentTime / video.duration);
      };

      video.addEventListener('ended', handleEnded);
      video.addEventListener('loadedmetadata', handleMetadata);
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('error', handleError);
      return () => {
        video.removeEventListener('ended', handleEnded);
        video.removeEventListener('loadedmetadata', handleMetadata);
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('error', handleError);
      };
    }, [noteMedia]);

    /** Play preferring sound; fall back muted rather than stall, and say so. */
    const play = useCallback((video: HTMLVideoElement) => {
      video.muted = mutedRef.current;
      let result: Promise<void> | undefined;
      try {
        result = video.play() as unknown as Promise<void> | undefined;
      } catch {
        return;
      }
      if (!result || typeof result.catch !== 'function') return;
      result.catch(() => {
        if (video.muted) return; // refused even muted: nothing left to try
        video.muted = true;
        handlersRef.current.onAutoplayBlocked?.();
        try {
          const retry = video.play() as unknown as Promise<void> | undefined;
          if (retry && typeof retry.catch === 'function') retry.catch(() => {});
        } catch {
          /* ignore */
        }
      });
    }, []);

    // New moment (or back to one): load it and start from the first frame
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      if (!src) {
        // A photo is showing. The element stays alive and hidden — unmounting
        // it is exactly what used to cost us the audio permission — but it
        // must hold NOTHING (BUG-125): a paused element that kept its src was
        // a loaded gun. Every touch ends in setMomentSound's rescue, whose
        // "nothing loaded = nothing to rescue" guard only bites if unloading
        // actually happened; without it the rescue resumed the PREVIOUS video
        // behind the photo (ghost audio), and its 'ended' then advanced past
        // the photo the reader was looking at. Same unload shape as
        // releaseSharedVideo — the audio grant survives it.
        if (!video.paused) video.pause();
        if (video.getAttribute('src')) {
          video.removeAttribute('src');
          try {
            video.load();
          } catch {
            /* jsdom has no load(); the removed src is what matters */
          }
        }
        return;
      }

      if (video.getAttribute('src') !== src) video.setAttribute('src', src);
      try {
        video.currentTime = 0;
      } catch {
        /* not seekable yet — playback starts at 0 anyway */
      }
      if (!pausedRef.current) play(video);
    }, [src, playbackKey, play]);

    // The viewer owns mute; keep the element honest without restarting it
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !src) return;
      video.muted = muted;
      if (!muted && !pausedRef.current && video.paused) play(video);
    }, [muted, src, play]);

    // Hold-to-pause, driven by the viewer
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !src) return;
      if (paused) {
        if (!video.paused) video.pause();
      } else if (video.paused) {
        play(video);
      }
    }, [paused, src, play]);

    // The click swallow is load-bearing in the conversation viewer, whose
    // backdrop closes on click: tapping the picture must not dismiss it.
    return (
      <div
        ref={hostRef}
        className="absolute inset-0 flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }
);

export default MomentVideo;
